import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HeartHandshake, Mail, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-auth-btn"
      className="w-full min-h-[56px] rounded-full border-2 border-border bg-white text-forest-900 font-bold text-lg flex items-center justify-center gap-3 hover:bg-cream transition-colors">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-6 h-6" />
      Continue with Google
    </button>
  );
}

export function AuthShell({ title, subtitle, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <div className="max-w-md w-full mx-auto px-4 py-8 flex-1 flex flex-col justify-center">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-forest-700 font-semibold mb-8 self-start" data-testid="back-home-btn">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-12 h-12 rounded-xl bg-terracotta flex items-center justify-center">
            <HeartHandshake className="text-white" size={28} strokeWidth={2.5} />
          </div>
          <span className="font-head font-extrabold text-2xl text-forest-900">Connect</span>
        </div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900">{title}</h1>
        <p className="text-lg text-forest-700 mt-2 mb-7">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

export { GoogleButton };

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to help a neighbour or find a hand.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="font-semibold text-forest-900 mb-1.5 block">Email</label>
          <div className="relative">
            <Mail size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest-500" strokeWidth={2.5} />
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full min-h-[56px] text-lg pl-12 pr-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-white" placeholder="you@example.com" />
          </div>
        </div>
        <div>
          <label className="font-semibold text-forest-900 mb-1.5 block">Password</label>
          <div className="relative">
            <Lock size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest-500" strokeWidth={2.5} />
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full min-h-[56px] text-lg pl-12 pr-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-white" placeholder="Your password" />
          </div>
          <div className="text-right mt-2">
            <Link to="/forgot-password" className="text-terracotta font-semibold" data-testid="forgot-link">Forgot password?</Link>
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full min-h-[56px] rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60">
          {busy ? "Logging in…" : "Log in"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="h-px bg-border flex-1" /><span className="text-forest-500 font-medium">or</span><div className="h-px bg-border flex-1" />
      </div>
      <GoogleButton />
      <p className="text-center text-forest-700 mt-6 text-lg">
        New here? <Link to="/register" className="text-terracotta font-bold" data-testid="go-register">Join free</Link>
      </p>
    </AuthShell>
  );
}
